/* Feb-2026 — RelationshipMapPanel.
 *
 * Collapsible "Relationship Map" card that renders just above the
 * per-character Bible inputs. The "Map Relationships" trigger itself
 * lives in CharacterBiblePanel's selector strip (above this card);
 * this component renders the resulting editable rows and lets the
 * author hand-edit them, add a pair manually, or delete one.
 *
 * iter-arc1: Added "Graph" view — an SVG circular force layout that
 * visualises every relationship as a coloured edge between character nodes.
 *
 * Each row: character pair + relationship_type (free text) + tension
 * (low/medium/high) + notes. AI recommendation/editing flows read
 * these same rows back as grounding (see routes_projects.py).
 */
import React, { useEffect, useState } from 'react';
import { ChevronRight, Heart, Plus, Trash2, Loader2, Maximize2, X, List, Network } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import RelationshipGraph from './RelationshipGraph';

const TENSION_DOT = { low: 'bg-emerald-500', medium: 'bg-amber-500', high: 'bg-rose-500' };

export default function RelationshipMapPanel({ rows, characters, saving, onSave }) {
  const [open, setOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [draft, setDraft] = useState(rows || []);
  const [dirty, setDirty] = useState(false);
  // iter-arc1: view toggle between table and graph
  const [view, setView] = useState('table');

  // Re-sync when a fresh AI generation (or another client) replaces rows.
  useEffect(() => { setDraft(rows || []); setDirty(false); }, [rows]);

  if (!rows) return null;   // bible/manuscript not analysed yet — strip's button handles the empty case.

  const patchRow = (i, patch) => {
    setDraft((d) => d.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const removeRow = (i) => {
    setDraft((d) => d.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const addRow = () => {
    if (characters.length < 2) return;
    setDraft((d) => [...d, {
      character_a_id: characters[0].id, character_a_name: characters[0].name,
      character_b_id: characters[1].id, character_b_name: characters[1].name,
      relationship_type: '', tension: 'medium', notes: '',
    }]);
    setDirty(true);
  };
  const setCharacter = (i, side, charId) => {
    const c = characters.find((x) => x.id === charId);
    if (!c) return;
    patchRow(i, side === 'a' ? { character_a_id: c.id, character_a_name: c.name } : { character_b_id: c.id, character_b_name: c.name });
  };

  const RowList = ({ maxH = true }) => (
    <div className={cn('flex flex-col gap-2', maxH && 'max-h-[280px] overflow-y-auto scroll-thin pr-0.5')}>
      {draft.map((r, i) => (
        <div key={i} className="flex flex-col gap-1.5 rounded-md border border-panel-border bg-card p-2" data-testid={`relationship-row-${i}`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <Select value={r.character_a_id} onValueChange={(v) => setCharacter(i, 'a', v)}>
              <SelectTrigger title={r.character_a_name} className="h-7 min-w-[90px] max-w-[150px] flex-1 text-[13px]" data-testid={`relationship-row-${i}-char-a`}><SelectValue /></SelectTrigger>
              <SelectContent>{characters.filter((c) => c.id !== r.character_b_id).map((c) => <SelectItem key={c.id} value={c.id} className="text-[13px]">{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[13.5px] text-muted-foreground">↔</span>
            <Select value={r.character_b_id} onValueChange={(v) => setCharacter(i, 'b', v)}>
              <SelectTrigger title={r.character_b_name} className="h-7 min-w-[90px] max-w-[150px] flex-1 text-[13px]" data-testid={`relationship-row-${i}-char-b`}><SelectValue /></SelectTrigger>
              <SelectContent>{characters.filter((c) => c.id !== r.character_a_id).map((c) => <SelectItem key={c.id} value={c.id} className="text-[13px]">{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input
              value={r.relationship_type}
              onChange={(e) => patchRow(i, { relationship_type: e.target.value })}
              placeholder="captor/captive, rivals, siblings…"
              className="h-7 flex-1 min-w-[140px] text-[13px]"
              data-testid={`relationship-row-${i}-type`}
            />
            <Select value={r.tension} onValueChange={(v) => patchRow(i, { tension: v })}>
              <SelectTrigger className="h-7 min-w-[86px] shrink-0 text-[13px]" data-testid={`relationship-row-${i}-tension`}>
                <span className={cn('mr-1 inline-block h-2 w-2 shrink-0 rounded-full', TENSION_DOT[r.tension] || TENSION_DOT.medium)} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low" className="text-[13px]">Low</SelectItem>
                <SelectItem value="medium" className="text-[13px]">Medium</SelectItem>
                <SelectItem value="high" className="text-[13px]">High</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} data-testid={`relationship-row-${i}-delete`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Input
            value={r.notes}
            onChange={(e) => patchRow(i, { notes: e.target.value })}
            placeholder="One line on the dynamic — e.g. 'high tension, she doesn't trust him yet'"
            className="h-7 text-[13px]"
            data-testid={`relationship-row-${i}-notes`}
          />
        </div>
      ))}
    </div>
  );

  const ActionBar = () => (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      {/* Table / Graph toggle */}
      <div className="flex items-center rounded-md border border-border overflow-hidden" data-testid="relationship-view-toggle">
        <button
          className={cn('flex items-center gap-1 px-2.5 py-1 text-[11.5px] transition-colors', view === 'table' ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/40')}
          onClick={() => setView('table')}
          data-testid="rel-view-table"
          title="Table view"
        >
          <List className="h-3 w-3" /> Table
        </button>
        <button
          className={cn('flex items-center gap-1 px-2.5 py-1 text-[11.5px] border-l border-border transition-colors', view === 'graph' ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/40')}
          onClick={() => setView('graph')}
          data-testid="rel-view-graph"
          title="Graph view"
        >
          <Network className="h-3 w-3" /> Graph
        </button>
      </div>
      {view === 'table' && (
        <Button variant="outline" size="sm" className="h-7 gap-1 text-[13px]" onClick={addRow} disabled={characters.length < 2} data-testid="relationship-map-add-row">
          <Plus className="h-3 w-3" /> Add pair
        </Button>
      )}
      {view === 'table' && dirty && (
        <Button size="sm" className="h-7 gap-1.5 text-[13px]" onClick={() => onSave(draft)} disabled={saving} data-testid="relationship-map-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Save changes
        </Button>
      )}
    </div>
  );

  return (
    <>
    <div className="shrink-0 border-b border-panel-border bg-secondary/20" data-testid="relationship-map-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="relationship-map-toggle"
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40"
      >
        <Heart className="h-3.5 w-3.5 text-primary" />
        <span className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">Relationship Map</span>
        <span className="text-[13px] text-muted-foreground">({draft.length})</span>
        <ChevronRight className={cn('ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-90')} />
        {open && draft.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
            className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            title="Expand to full screen"
            data-testid="relationship-map-fullscreen"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {draft.length === 0 ? (
            <p className="rounded-md border border-dashed border-panel-border/70 bg-card/60 p-2 text-[13px] text-muted-foreground">
              No relationships mapped yet. Click "Map Relationships" above to have AI propose rows from your Bible + manuscript, or add one manually below.
            </p>
          ) : view === 'graph' ? (
            <RelationshipGraph rows={draft} characters={characters} />
          ) : (
            <RowList maxH={true} />
          )}
          <ActionBar />
        </div>
      )}
    </div>

    {/* Fullscreen dialog */}
    <Dialog open={fullscreen} onOpenChange={setFullscreen}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="relationship-map-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Heart className="h-4 w-4 text-primary" />
            Relationship Map
            <span className="text-[13px] font-normal text-muted-foreground">({draft.length} pairs)</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto scroll-thin py-1 pr-1">
          {draft.length === 0 ? (
            <p className="rounded-md border border-dashed border-panel-border/70 bg-card/60 p-3 text-[13px] text-muted-foreground">
              No relationships mapped yet.
            </p>
          ) : view === 'graph' ? (
            <RelationshipGraph rows={draft} characters={characters} />
          ) : (
            <RowList maxH={false} />
          )}
        </div>
        <div className="shrink-0 border-t border-panel-border pt-3">
          <ActionBar />
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}